import React, { type PropsWithChildren, useCallback, useEffect } from 'react';
import { useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, {
  Easing,
  FadeIn,
  FadeInDown,
  LinearTransition,
  SlideInDown,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';
import { GlassView } from '@garnish/glass';

import { useCart, useIsLiquidGlassEnabled } from '../../../../hooks';
import { useNavigateBack } from '../../../../navigation';
import {
  QUICK_ADD_ENTRANCE_DURATION,
  QUICK_ADD_LAYOUT_DURATION,
} from '../../QuickAddScreen.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const QuickAddModal = (props: PropsWithChildren) => {
  const { children } = props;

  const navigateBack = useNavigateBack();
  const navigation = useNavigation();
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();
  const { pendingCartMutations } = useCart();
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  // ─── Derived ─────────────────────────────────────────────────────────

  const isAddingToBag = pendingCartMutations.has('add-line-item');
  const isSheet = currentBreakpoint.isXS;

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (isAddingToBag) return;

    navigateBack();
  }, [isAddingToBag, navigateBack]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    navigation.setOptions({ gestureEnabled: !isAddingToBag });
  }, [isAddingToBag, navigation]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    isSheet ? styles.containerSheet : styles.containerDialog,
  ];

  const sheetDynamicStyles = useStyle(
    () => (isSheet ? { paddingBottom: SHEET_BOTTOM_SPACING + safeAreaBottomInset } : undefined),
    [isSheet, safeAreaBottomInset],
  );

  const panelStyles = [
    styles.panel,
    isSheet ? styles.panelSheet : styles.panelDialog,
    isLiquidGlassEnabled ? styles.panelGlass : undefined,
    sheetDynamicStyles,
  ];

  const glassStyles = [styles.glass, isSheet ? styles.glassSheet : styles.glassDialog];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <Animated.View entering={BACKDROP_ENTERING} style={styles.backdrop}>
        <TouchableOpacity
          accessibilityLabel={formatMessage(messages.dismissAccessibilityLabel)}
          accessibilityRole="button"
          activeOpacity={1}
          style={styles.backdropPressable}
          onPress={handleClose}
        />
      </Animated.View>

      <Animated.View
        entering={isSheet ? SHEET_ENTERING : DIALOG_ENTERING}
        layout={PANEL_LAYOUT_TRANSITION}
        style={panelStyles}
      >
        {isLiquidGlassEnabled ? (
          <GlassView
            glassEffectStyle="regular"
            pointerEvents="none"
            style={glassStyles}
            tintColor={PANEL_GLASS_TINT_COLOR}
          />
        ) : null}

        {children}
      </Animated.View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SHEET_BOTTOM_SPACING = Platform.select({
  ios: theme.spacing['2'],
  default: theme.spacing['6'],
});

const ENTRANCE_EASING = Easing.out(Easing.cubic);

const DIALOG_WIDTH = 584;
const DIALOG_MAX_HEIGHT = 672;
const DIALOG_MIN_HEIGHT = 240;

const PANEL_GLASS_TINT_COLOR = 'rgba(255, 255, 255, 0.6)';
const PANEL_LAYOUT_TRANSITION = LinearTransition.duration(QUICK_ADD_LAYOUT_DURATION);

const BACKDROP_ENTERING = FadeIn.duration(QUICK_ADD_ENTRANCE_DURATION);
const SHEET_ENTERING = SlideInDown.duration(QUICK_ADD_ENTRANCE_DURATION).easing(ENTRANCE_EASING);
const DIALOG_ENTERING = FadeInDown.duration(QUICK_ADD_ENTRANCE_DURATION).easing(ENTRANCE_EASING);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerSheet: {
    justifyContent: 'flex-end',
  },
  containerDialog: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing['10'],
  },
  backdrop: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  backdropPressable: {
    flex: 1,
  },
  panel: {
    backgroundColor: theme.colors.CREAM,
  },
  panelSheet: {
    width: '100%',
    borderTopLeftRadius: theme.radius.large,
    borderTopRightRadius: theme.radius.large,
    paddingTop: theme.spacing['10'],
    paddingHorizontal: theme.spacing['4'],
  },
  panelDialog: {
    width: DIALOG_WIDTH,
    maxWidth: '100%',
    maxHeight: DIALOG_MAX_HEIGHT,
    minHeight: DIALOG_MIN_HEIGHT,
    justifyContent: 'center',
    borderRadius: theme.radius.large,
    paddingVertical: theme.spacing['8'],
    paddingHorizontal: theme.spacing['6'],
    boxShadow: '0px 12px 48px 8px rgba(14, 21, 14, 0.12)',
  },
  panelGlass: {
    backgroundColor: 'transparent',
  },
  glass: {
    ...StyleSheet.absoluteFill,
  },
  glassSheet: {
    borderTopLeftRadius: theme.radius.large,
    borderTopRightRadius: theme.radius.large,
  },
  glassDialog: {
    borderRadius: theme.radius.large,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dismissAccessibilityLabel: {
    defaultMessage: 'Dismiss',
    description: 'Quick Add | Bottom sheet | Dismiss backdrop accessibility label',
  },
});
